/**
 * CadastrarLavaJatoPage — cadastro self-serve de novo tenant.
 *
 * Rota pública (sem auth): /cadastrar-lava-jato
 *
 * Formulário em 3 etapas:
 *   1. Dados do lava-jato (nome, CNPJ, subdomínio desejado)
 *   2. Dados do admin (nome, email, senha, CPF, WhatsApp)
 *   3. Aceite de termos + confirmação
 *
 * Chama a Edge Function criar-tenant-self-serve.
 * Tenant criado em status EM_PROVISIONAMENTO (ativo em até 1 dia útil após revisão).
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  User,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';

// ============================================================
// Schemas de validacao
// ============================================================

const esquemaEtapa1 = z.object({
  nom_tenant: z.string().min(3, 'Nome mínimo 3 caracteres'),
  num_cnpj: z
    .string()
    .transform((v) => v.replace(/\D/g, ''))
    .pipe(z.string().length(14, 'CNPJ deve ter 14 dígitos')),
  des_subdominio: z
    .string()
    .min(3)
    .max(32)
    .regex(/^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/, 'Apenas letras minúsculas, números e hífen'),
});

const esquemaEtapa2 = z.object({
  admin_nome: z.string().min(3, 'Nome mínimo 3 caracteres'),
  admin_email: z.string().email('E-mail inválido'),
  admin_senha: z.string().min(10, 'Senha mínimo 10 caracteres'),
  admin_cpf: z
    .string()
    .transform((v) => v.replace(/\D/g, ''))
    .pipe(z.string().length(11, 'CPF deve ter 11 dígitos')),
  admin_whatsapp: z.string().optional(),
});

// Etapa 3 usa checkbox nativo sem react-hook-form (sem schema necessario)

type Etapa1 = z.infer<typeof esquemaEtapa1>;
type Etapa2 = z.infer<typeof esquemaEtapa2>;

// ============================================================
// Helpers de mascara
// ============================================================

function mascaraCnpj(v: string) {
  return v
    .replace(/\D/g, '')
    .slice(0, 14)
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

function mascaraCpf(v: string) {
  return v
    .replace(/\D/g, '')
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1-$2');
}

// ============================================================
// Componente principal
// ============================================================

type Etapa = 1 | 2 | 3 | 'sucesso';

interface Dados {
  etapa1?: Etapa1;
  etapa2?: Etapa2;
}

export function CadastrarLavaJatoPage() {
  const [etapa, setEtapa] = useState<Etapa>(1);
  const [dados, setDados] = useState<Dados>({});
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [resultadoCodTenant, setResultadoCodTenant] = useState('');

  const form1 = useForm<Etapa1>({ resolver: zodResolver(esquemaEtapa1) });
  const form2 = useForm<Etapa2>({ resolver: zodResolver(esquemaEtapa2) });

  async function enviar() {
    if (!dados.etapa1 || !dados.etapa2) return;
    setEnviando(true);
    setErroEnvio(null);
    try {
      const { data, error } = await supabase.functions.invoke('criar-tenant-self-serve', {
        body: {
          ...dados.etapa1,
          ...dados.etapa2,
          ind_aceite_termos: true,
        },
      });
      if (error) throw new Error(error.message);
      const res = data as { ok: boolean; cod_tenant: string; erros?: string[] };
      if (!res.ok) {
        setErroEnvio((res.erros ?? ['Erro desconhecido']).join(', '));
        return;
      }
      setResultadoCodTenant(res.cod_tenant);
      setEtapa('sucesso');
    } catch (e) {
      setErroEnvio(e instanceof Error ? e.message : 'Erro ao enviar cadastro');
    } finally {
      setEnviando(false);
    }
  }

  const ETAPAS = [
    { num: 1 as const, label: 'Lava-jato', icon: Building2 },
    { num: 2 as const, label: 'Responsavel', icon: User },
    { num: 3 as const, label: 'Confirmar', icon: ShieldCheck },
  ];

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mb-3 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg">
            <Building2 className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Cadastre seu lava-jato</h1>
          <p className="mt-1 text-sm text-slate-500">
            Comece em minutos. Período de avaliação gratuito.
          </p>
        </div>

        {/* Indicador de etapas */}
        {etapa !== 'sucesso' && (
          <div className="mb-6 flex items-center justify-center gap-2">
            {ETAPAS.map((e, idx) => (
              <div key={e.num} className="flex items-center gap-2">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                    etapa === e.num
                      ? 'bg-blue-600 text-white'
                      : (etapa as number) > e.num
                        ? 'bg-emerald-500 text-white'
                        : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {(etapa as number) > e.num ? <CheckCircle2 className="h-4 w-4" /> : e.num}
                </div>
                <span
                  className={`text-xs font-medium ${etapa === e.num ? 'text-slate-900' : 'text-slate-400'}`}
                >
                  {e.label}
                </span>
                {idx < ETAPAS.length - 1 && <div className="h-px w-6 bg-slate-200" />}
              </div>
            ))}
          </div>
        )}

        <AnimatePresence mode="wait">
          {/* Etapa 1 */}
          {etapa === 1 && (
            <motion.div
              key="etapa1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card className="shadow-lg">
                <CardContent className="space-y-4 p-6">
                  <h2 className="font-semibold text-slate-900">Dados do lava-jato</h2>
                  <form
                    onSubmit={form1.handleSubmit((d) => {
                      setDados((prev) => ({ ...prev, etapa1: d }));
                      setEtapa(2);
                    })}
                    className="space-y-4"
                  >
                    <div className="space-y-1.5">
                      <Label htmlFor="nom_tenant">Nome do estabelecimento</Label>
                      <Input
                        id="nom_tenant"
                        placeholder="Lava-rápido Exemplo"
                        {...form1.register('nom_tenant')}
                      />
                      {form1.formState.errors.nom_tenant && (
                        <p className="text-xs text-red-500">
                          {form1.formState.errors.nom_tenant.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="num_cnpj">CNPJ</Label>
                      <Input
                        id="num_cnpj"
                        placeholder="00.000.000/0001-00"
                        {...form1.register('num_cnpj')}
                        onChange={(e) => {
                          e.target.value = mascaraCnpj(e.target.value);
                          form1.register('num_cnpj').onChange(e);
                        }}
                      />
                      {form1.formState.errors.num_cnpj && (
                        <p className="text-xs text-red-500">
                          {form1.formState.errors.num_cnpj.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="des_subdominio">Subdomínio desejado</Label>
                      <div className="flex items-center">
                        <Input
                          id="des_subdominio"
                          placeholder="meu-lavarapido"
                          className="rounded-r-none"
                          {...form1.register('des_subdominio')}
                        />
                        <span className="inline-flex h-10 items-center rounded-r-md border border-l-0 bg-slate-100 px-3 text-xs text-slate-500">
                          .gestao.octawash.com.br
                        </span>
                      </div>
                      {form1.formState.errors.des_subdominio && (
                        <p className="text-xs text-red-500">
                          {form1.formState.errors.des_subdominio.message}
                        </p>
                      )}
                      <p className="text-xs text-slate-500">
                        Somente letras minúsculas, números e hífen (3–32 chars)
                      </p>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Próximo <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Etapa 2 */}
          {etapa === 2 && (
            <motion.div
              key="etapa2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card className="shadow-lg">
                <CardContent className="space-y-4 p-6">
                  <h2 className="font-semibold text-slate-900">Dados do responsavel</h2>
                  <form
                    onSubmit={form2.handleSubmit((d) => {
                      setDados((prev) => ({ ...prev, etapa2: d }));
                      setEtapa(3);
                    })}
                    className="space-y-4"
                  >
                    <div className="space-y-1.5">
                      <Label htmlFor="admin_nome">Nome completo</Label>
                      <Input id="admin_nome" {...form2.register('admin_nome')} />
                      {form2.formState.errors.admin_nome && (
                        <p className="text-xs text-red-500">
                          {form2.formState.errors.admin_nome.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="admin_email">E-mail</Label>
                      <Input
                        id="admin_email"
                        type="email"
                        placeholder="voce@empresa.com.br"
                        {...form2.register('admin_email')}
                      />
                      {form2.formState.errors.admin_email && (
                        <p className="text-xs text-red-500">
                          {form2.formState.errors.admin_email.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="admin_senha">Senha (mín. 10 caracteres)</Label>
                      <Input
                        id="admin_senha"
                        type="password"
                        autoComplete="new-password"
                        {...form2.register('admin_senha')}
                      />
                      {form2.formState.errors.admin_senha && (
                        <p className="text-xs text-red-500">
                          {form2.formState.errors.admin_senha.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="admin_cpf">CPF</Label>
                      <Input
                        id="admin_cpf"
                        placeholder="000.000.000-00"
                        {...form2.register('admin_cpf')}
                        onChange={(e) => {
                          e.target.value = mascaraCpf(e.target.value);
                          form2.register('admin_cpf').onChange(e);
                        }}
                      />
                      {form2.formState.errors.admin_cpf && (
                        <p className="text-xs text-red-500">
                          {form2.formState.errors.admin_cpf.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="admin_whatsapp">WhatsApp (opcional)</Label>
                      <Input
                        id="admin_whatsapp"
                        placeholder="5511999999999"
                        {...form2.register('admin_whatsapp')}
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        onClick={() => setEtapa(1)}
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
                      >
                        Próximo <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Etapa 3 — Confirmacao */}
          {etapa === 3 && (
            <motion.div
              key="etapa3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card className="shadow-lg">
                <CardContent className="space-y-5 p-6">
                  <h2 className="font-semibold text-slate-900">Confirmar cadastro</h2>

                  <div className="rounded-lg bg-slate-50 p-4 text-sm">
                    <p className="mb-1 font-semibold text-slate-700">{dados.etapa1?.nom_tenant}</p>
                    <p className="text-slate-500">
                      {dados.etapa1?.des_subdominio}.gestao.octawash.com.br
                    </p>
                    <p className="mt-2 text-slate-600">{dados.etapa2?.admin_nome}</p>
                    <p className="text-slate-500">{dados.etapa2?.admin_email}</p>
                  </div>

                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-slate-300"
                      id="aceite"
                    />
                    <span className="text-sm text-slate-600">
                      Li e aceito os{' '}
                      <a
                        href="https://octawash.com.br/termos"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        Termos de Uso
                      </a>{' '}
                      e a{' '}
                      <a
                        href="https://octawash.com.br/privacidade"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        Política de Privacidade
                      </a>
                      .
                    </span>
                  </label>

                  {erroEnvio && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                      {erroEnvio}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setEtapa(2)}
                      disabled={enviando}
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
                    </Button>
                    <Button
                      className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
                      onClick={() => {
                        const check = document.getElementById('aceite') as HTMLInputElement;
                        if (!check.checked) {
                          setErroEnvio('Aceite os termos para continuar.');
                          return;
                        }
                        setErroEnvio(null);
                        void enviar();
                      }}
                      disabled={enviando}
                    >
                      {enviando ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="mr-2 h-4 w-4" /> Enviar cadastro
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Sucesso */}
          {etapa === 'sucesso' && (
            <motion.div
              key="sucesso"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <Card className="shadow-lg">
                <CardContent className="space-y-5 p-8 text-center">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                    <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Cadastro recebido!</h2>
                    <p className="mt-1 text-sm text-slate-500">
                      Nossa equipe vai revisar e ativar seu acesso em até 1 dia útil.
                    </p>
                  </div>

                  <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-700">
                    <p className="mb-2 font-semibold">Próximos passos:</p>
                    <ol className="list-inside list-decimal space-y-1 text-left">
                      <li>Verifique seu e-mail e confirme a conta</li>
                      <li>Aguarde o e-mail de ativação (até 1 dia útil)</li>
                      <li>
                        Acesse <strong>{resultadoCodTenant}.gestao.octawash.com.br</strong>
                      </li>
                    </ol>
                  </div>

                  <Link
                    to="/login"
                    className="inline-flex items-center text-sm font-medium text-blue-600 hover:underline"
                  >
                    Ir para login <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        <p className="mt-6 text-center text-xs text-slate-400">
          Já tem uma conta?{' '}
          <Link to="/login" className="text-blue-600 hover:underline">
            Fazer login
          </Link>
        </p>
      </div>
    </div>
  );
}
